/**
 * Workflow task / work-queue hook (FND-006).
 *
 * Wraps the workflow control-plane read surface:
 *   - GET /api/v1/tasks?queue=&cursor=&limit=  → TaskPage (SLA-ordered)
 *
 * Tasks are returned ordered by SLA urgency (breached first) then priority,
 * so the queue page can render an actionable, ranked work list. An optional
 * `queue` filter narrows the list to a single role queue (e.g. `pas`).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { Task, TaskPage } from '../api/types';

interface UseTasksResult {
  tasks: Task[];
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

/** Loads the tenant's SLA-ranked task queue, optionally filtered to one role. */
export function useTasks(queue?: string): UseTasksResult {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    const path = queue ? `/tasks?queue=${encodeURIComponent(queue)}` : '/tasks';
    apiClient
      .get<TaskPage>(path)
      .then((page) => {
        setTasks(page.items ?? []);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setTasks([]);
      })
      .finally(() => setIsLoading(false));
  }, [queue]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { tasks, isLoading, error, refresh };
}
